import React, { useCallback, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import Header from "../../Navbar/Header";
import Sidebar from "../sidebar/Sidebar";
import Pagination from "../../../component/Pagination";
import StyledDataTable from "../../../component/resuable/StyledComponent";
import LoadingSpinner from "../../../component/resuable/LoadingSpinner";
import { apiGet, apiPut } from "../../../service/apiService";
import "./Jot.css";

const getHelpData = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  return [];
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  return value;
};

const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const getDocumentExtension = (documentUrl) => {
  const cleanUrl = String(documentUrl).split("?")[0].split("#")[0];
  return cleanUrl.includes(".") ? cleanUrl.split(".").pop().toLowerCase() : "";
};

const statusOptions = ["Pending", "Completed", "Rejected"];
const pageSize = 10;

const defaultPagination = {
  page: 1,
  per_page: pageSize,
  total_pages: 0,
  total_records: 0,
};

const JotHelp = () => {
  const [helpData, setHelpData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(defaultPagination);
  const [selectedHelp, setSelectedHelp] = useState(null);
  const [status, setStatus] = useState("Pending");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchHelpData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiGet("jot/help/list", {
        page: currentPage,
        per_page: pageSize,
        search: search.trim(),
      });

      setHelpData(getHelpData(response?.data));
      setPagination(response?.data?.pagination || defaultPagination);
    } catch (error) {
      setHelpData([]);
      setPagination(defaultPagination);
      toast.error("Failed to fetch help list");
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  useEffect(() => {
    fetchHelpData();
  }, [fetchHelpData]);

  const handleSearchChange = (event) => {
    setCurrentPage(1);
    setSearch(event.target.value);
  };

  const handleViewClick = (row) => {
    setSelectedHelp(row);
    setStatus(row.status || "Pending");
    setRemarks(row.remarks || "");
  };

  const handleBack = () => {
    setSelectedHelp(null);
    setStatus("Pending");
    setRemarks("");
  };

  const renderDocumentPreview = (documentUrl) => {
    if (!documentUrl) {
      return <div className="jot-help-readonly">N/A</div>;
    }

    const url = String(documentUrl).trim();
    const extension = getDocumentExtension(url);

    if (imageExtensions.includes(extension)) {
      return (
        <div className="jot-help-document-preview">
          <img src={url} alt="Help document preview" className="jot-help-document-image" />
        </div>
      );
    }

    if (extension === "pdf") {
      return (
        <div className="jot-help-document-preview jot-help-document-frame-wrap">
          <iframe src={url} title="Help document preview" className="jot-help-document-frame" />
        </div>
      );
    }

    return (
      <div className="jot-help-readonly">
        <a href={url} target="_blank" rel="noreferrer">
          Open document
        </a>
      </div>
    );
  };

  const handleSave = async () => {
    if (!selectedHelp?.id) {
      toast.error("Invalid help request");
      return;
    }

    const payload = {
      status,
      remarks,
    };

    setSaving(true);

    try {
      const response = await apiPut(`jot/help/${selectedHelp.id}`, payload);

      if (response?.status === 200) {
        toast.success("Help request updated successfully");

        await fetchHelpData();

        handleBack();
      } else {
        toast.error("Failed to update help request");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || "Something went wrong while saving");
    } finally {
      setSaving(false);
    }
  };



  const columns = [
    {
      name: "S.No",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: true,
      width: "7%",
    },
    {
      name: "Student Name",
      selector: (row) => formatValue(row.student_name),
      sortable: true,
      width: "15%",
    },
    {
      name: "Email",
      selector: (row) => formatValue(row.email),
      sortable: true,
      width: "18%",
    },
    {
      name: "Phone",
      selector: (row) => formatValue(row.phone),
      sortable: true,
      width: "12%",
    },
    {
      name: "Challenges",
      selector: (row) => formatValue(row.challenges),
      sortable: true,
      width: "14%",
    },
    {
      name: "Status",
      selector: (row) => formatValue(row.status),
      sortable: true,
      width: "11%",
    },
    {
      name: "Remarks",
      selector: (row) => formatValue(row.remarks),
      sortable: true,
      wrap: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Button className="table-viewcustom-button" onClick={() => handleViewClick(row)} title="View help details">
          <i className="fas fa-eye px-2" />
        </Button>
      ),
      button: true,
      width: "8%",
    },
  ];

  const renderDetails = () => (
    <div className="card custom-card jot-help-details">
      <div className="jot-help-details-header">
        <div>
          <h5 className="m-0">Help Details</h5>
          <span className="jot-help-subtitle">{formatValue(selectedHelp.student_name)}</span>
        </div>

        <div className="jot-help-actions">
          <Button className="back-btn" onClick={handleBack} disabled={saving}>
            Back
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="p-3">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label">Student Name</label>
            <div className="jot-help-readonly">{formatValue(selectedHelp.student_name)}</div>
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Email</label>
            <div className="jot-help-readonly">{formatValue(selectedHelp.email)}</div>
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Phone</label>
            <div className="jot-help-readonly">{formatValue(selectedHelp.phone)}</div>
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Challenges</label>
            <div className="jot-help-readonly">{formatValue(selectedHelp.challenges)}</div>
          </div>

          <div className="col-12">
            <label className="form-label">Description</label>
            <div className="jot-help-readonly jot-help-description">{formatValue(selectedHelp.description)}</div>
          </div>

          <div className="col-12">
            <label className="form-label">Document</label>
            {renderDocumentPreview(selectedHelp.document)}
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Status</label>
            <select className="form-control" value={status} onChange={(event) => setStatus(event.target.value)}>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}

              {!statusOptions.includes(status) && <option value={status}>{status}</option>}
            </select>
          </div>

          <div className="col-12">
            <label className="form-label">Remarks</label>
            <textarea
              className="form-control jot-help-remarks"
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              placeholder="Enter remarks"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="row">
        <Header />

        <div className="col-2 p-0">
          <Sidebar />
        </div>

        <div className="col-10 mt-2 ps-3">
          {selectedHelp ? (
            renderDetails()
          ) : (
            <div className="card custom-card">
              {loading && <LoadingSpinner />}

              <div className="row align-items-center p-3">
                <div className="col-12 col-md-6">
                </div>

                <div className="col-12 col-md-4 offset-md-2 mt-3 mt-md-0">
                  <input
                    className="form-control search-btn"
                    placeholder="Search"
                    value={search}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>

              <div className="row p-3">
                <div className="col-12">
                  <StyledDataTable
                    columns={columns}
                    data={helpData}
                    highlightOnHover
                    responsive
                    paginationDefaultPage={currentPage}
                    persistTableHead
                    noDataComponent="No help requests found"
                  />
                </div>
              </div>

              {pagination?.total_records > 0 && (
                <Pagination
                  className="pagination-bar"
                  currentPage={pagination?.page || currentPage}
                  totalCount={pagination?.total_records || 0}
                  pageSize={pagination?.per_page || pageSize}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JotHelp;

